import React, { useContext, useState, useEffect, memo } from "react";
import { Box } from "@mui/material";
import moment from "moment";
import { isEmpty } from "lodash-es";
import { analysisColors } from "features/configure/chartColors";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import { useOutletContext } from "react-router";
import { KeyMatrixContext } from "../context/KeyMatrixProvider";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import axios from "axios";
import { apiUrl } from "features/configure";
import LoadingIcon from "components/LoadingComponent/LoadingIcon";
import { formatDate } from "features/helpers/utils";
import { useIntl } from "react-intl";
import { CohortTable } from "@metacrm/metacrm-material-ui/dist/Table";
import EmptyIconStatus from "components/EmptyState/EmptyIconStatus";
import { Loading } from "features/common";

const CohortChart = ({ dynamicApiUrl, dynamicEntityName }) => {
  const heatColor = analysisColors.small[0];
  const [cohortRetention, setCohortRetention] = useState();
  const { formatMessage } = useIntl();
  const [entityName] = useOutletContext();
  const { granularity, range, isLoading, setIsLoading } =
    useContext(KeyMatrixContext);
  const dispatchSnackbar = useDispatchSnackbar();

  const cohortOptionMapping = {
    daily: "days",
    weekly: "weeks",
    monthly: "months",
  };

  async function fetchCohortRetention() {
    try {
      setIsLoading((prev) => ({ ...prev, cohortChart: true }));

      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/dashboard/${dynamicEntityName}/cohortRetention`,
        {
          granularity,
          range,
        }
      );
      setCohortRetention(data.data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setIsLoading((prev) => ({ ...prev, cohortChart: false }));
    }
  }
  useEffect(() => {
    fetchCohortRetention();
  }, [granularity, range]);

  function isDataEmpty(data) {
    if (!data) return true;
    return Object.values(data).every((dates) =>
      dates.every((value) => value === "N/A")
    );
  }

  const isNoData = isDataEmpty(cohortRetention);

  return (
    <Box bgcolor="white" p="16px" color="#555" borderRadius="4px">
      <Box className="item-flex" mb="20px">
        <Box fontSize={16} mr="4px">
          {formatMessage({ id: "websiteTraffic.chart.cohortRetention" })}
        </Box>
        <IconTooltip content="Displays the percentage of unique wallets that return in subsequent periods after their first visit. The cohort period is determined by the selected time frame (Daily/Weekly/Monthly). Each row represents a cohort of new wallets, and columns show their retention rates over time." />
      </Box>
      <Box sx={{ height: "300px" }}>
        {isLoading.cohortChart ? (
          <Box sx={{ position: "relative", height: "100%" }}>
            <Loading open={true} fullScreen={false} isChart={true} />
          </Box>
        ) : isNoData ? (
          <EmptyIconStatus sx={{ height: "100%" }} />
        ) : (
          <Box
            sx={{
              width: "100%",
              overflow: "auto",
              margin: "0 auto",
              flex: 1,
            }}
          >
            <CohortTable
              data={cohortRetention}
              color={heatColor}
              unit={cohortOptionMapping[granularity]}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CohortChart;
